import React, { useState, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { Col, Row } from 'reactstrap';

import Widget from '../../components/Widget';
import ContentContainer from '../../components/ContentContainer';
import PageTitle from '../../components/PageTitle';
import CompanyDetailsWidget from '../../components/widgets/CompanyDetailsWidget';
import withUser from '../../hoc/withUser';
import Spinner from '../../components/Spinner';
import CollapsedBox from '../../components/CollapsedBox';
import InfoMessage from '../../components/InfoMessage';

import '../CompanyAdmin/components/DashboardContent/styles.scss';
import AddressCompanyIcon from '../../assets/location_map.svg';
import PhoneIcon from '../../assets/phone-icon.svg';
import WorldWideIcon from '../../assets/www-icon.svg';
import InfoIcon from '../../assets/information_stroke.svg';
import BranchAddressIcon from '../../assets/location_branch.svg';
import StyledLink from '../../components/StyledLink';
import { formatLink } from '../../utils/helpers';
import { useStore } from '../../store';

const CompanyPage = observer(({ user, authToken }) => {
  const {
    rootStore: {
      profileStore,
      profileStore: { companyStore },
    },
  } = useStore();

  const [showLoader, setShowLoader] = useState(true);

  const { t, i18n } = useTranslation();

  useEffect(() => {
    const fetchData = async () => {
      if (!profileStore.profileData) {
        await profileStore.initProfile(user, authToken, i18n);
      }

      await profileStore.loadUser();
      await companyStore.getAdminRequestStatus();

      setTimeout(() => {
        setShowLoader(false);
      }, 100);
    };

    fetchData();
  }, [profileStore, companyStore, user, authToken, i18n]);

  if (showLoader) return <Spinner pageLoading />;

  if (profileStore.companyAddress === '') {
    return (
      <>
        <ContentContainer>
          <PageTitle titleKey={t('companyInfo.title')} subtitleKey={t('companyInfo.subtitle')} />
          <Row>
            <Col>
              <Widget address={profileStore.companyAddress}>
                <CompanyDetailsWidget
                  notApprovedMessage={t('companyInfo.notApprovedMessage')}
                  notApprovedLink={t('companyInfo.notApprovedLink')}
                />
              </Widget>
            </Col>
          </Row>
        </ContentContainer>
      </>
    );
  } else {
    return (
      <>
        <ContentContainer>
          <PageTitle titleKey={profileStore.company?.name} subtitleKey={t('companyInfo.summary')} />
          <Row>
            <Col>
              <li
                className="text-field-with-icon"
                style={{
                  backgroundImage: `url(${AddressCompanyIcon})`,
                }}
              >
                {[t('companyInfo.hqAddress'), profileStore.companyAddress].join(': ')}
              </li>
              {profileStore.branchAddress && (
                <li
                  className="text-field-with-icon"
                  style={{
                    backgroundImage: `url(${BranchAddressIcon})`,
                  }}
                >
                  {[t('companyInfo.branchAddress'), profileStore.branchAddress].join(': ')}
                </li>
              )}
              <li
                className="text-field-with-icon"
                style={{
                  backgroundImage: `url(${PhoneIcon})`,
                }}
              >
                {profileStore.company?.phone}
              </li>
              <li
                className={`text-field-with-icon variant-link`}
                style={{
                  backgroundImage: `url(${WorldWideIcon})`,
                }}
              >
                <StyledLink href={formatLink(profileStore.company?.url)} target="_blank" rel="noopener noreferrer">
                  {profileStore.company?.url}
                </StyledLink>
              </li>
              <p className="light-paragraph" style={{ marginTop: 16, marginLeft: 0 }}>
                {profileStore.company?.description}
              </p>
              <li
                className={`text-field-with-icon variant-link`}
                style={{
                  backgroundImage: `url(${InfoIcon})`,
                  backgroundSize: '19px !important',
                }}
              >
                <StyledLink
                  href={'https://moneyhouse.ch/company/' + profileStore.company?.uid}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t('companyInfo.disclaimer')}
                </StyledLink>
              </li>
              <p className="light-paragraph" style={{ marginTop: 0, marginLeft: 0, marginBottom: '50px' }}>
                {t('companyDashboard.moneyHousePolicy')}
              </p>
              {companyStore.requestStatusMessageKey ? (
                <InfoMessage text={t(companyStore.requestStatusMessageKey)} />
              ) : (
                <CollapsedBox
                  title={t('companyInfo.collapsedBoxTitle')}
                  buttonTitle={t('companyInfo.buttonTitle')}
                  onConfirmRequest={() => companyStore.requestCompanyAdmin(t)}
                  listTitle={t('companyInfo.listTitle')}
                  listItemsText={{
                    first: t('companyInfo.listItems.first'),
                    second: t('companyInfo.listItems.second'),
                    third: t('companyInfo.listItems.third'),
                  }}
                  modalTitle={t('companyInfo.confirmationTitle')}
                  modalDescription={t('companyInfo.confirmationDescription')}
                  disabledRequestButton={companyStore.isLoadingAdminRequest}
                />
              )}
            </Col>
          </Row>
        </ContentContainer>
      </>
    );
  }
});

export default withUser(CompanyPage);
